import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { CheckCircle2, ClipboardEdit, Download, FilePlus2, Pencil, Send, ShieldCheck, Trash2 } from "lucide-react";

import type { AcademicTerm, AcademicYear } from "../../services/academicSetupService";
import { examinationService, type Exam, type ExamType } from "../../services/examinationService";
import { ReasonPanel, errorLine } from "../inventory/InventoryParts";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { PaperForm, type ClassOption, type SubjectOption } from "./ExamForms";
import { ACTION_LABELS, STAGES, day, markedLabel, nextAction, paperConfig, sheetDraft, sheetSummary, stageOf, timeRange, typeLabel, type Abilities } from "./examLogic";

/** Where the paper's results stand, step by step. */
function StageTrack({ exam }: { exam: Exam }) {
  const reached = STAGES.findIndex((stage) => stage.key === exam.results_status);
  const who: Record<string, string | undefined> = { moderated: exam.moderated_by_name, approved: exam.approved_by_name };
  return (
    <ol className="grid gap-2 sm:grid-cols-5">
      {STAGES.map((stage, index) => (
        <li key={stage.key} className={`rounded-lg border px-3 py-2 text-xs ${index <= reached ? "border-primary-200 bg-primary-50 text-primary-900" : "border-gray-200 text-gray-400"}`}>
          <span className="block font-semibold">{index + 1}. {["Marking", "Submitted", "Moderated", "Approved", "Published"][index]}</span>
          {index <= reached && who[stage.key] && <span className="block truncate text-primary-700">{who[stage.key]}</span>}
        </li>
      ))}
    </ol>
  );
}

/** Marks and grades once a sheet has marks, from the candidates' results. */
function ResultsSummary({ exam }: { exam: Exam }) {
  const candidates = useQuery({ queryKey: ["exams", "candidates", exam.id], queryFn: () => examinationService.getPaperCandidates(exam.id), enabled: exam.marked_count > 0 });
  if (!exam.marked_count) return null;
  if (candidates.isLoading) return <p className="text-sm text-gray-500">Loading the marks…</p>;
  const summary = sheetSummary(sheetDraft(candidates.data ?? []), exam.total_marks, exam.passing_marks);
  return (
    <section className="space-y-2">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Grades</h4>
      <div className="sheet-wrap">
        <table className="sheet">
          <thead>
            <tr>
              {Object.keys(summary.grades).map((grade) => <th key={grade} className="text-right">{grade}</th>)}
              <th className="text-right">Absent</th>
              <th className="text-right">Not marked</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              {Object.values(summary.grades).map((count, index) => <td key={index} className="text-right tabular-nums">{count}</td>)}
              <td className="text-right tabular-nums">{summary.absent}</td>
              <td className="text-right tabular-nums">{summary.blank}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** Every paper you can see and where its results stand; open one to move it on. */
export function PapersPanel({ exams, types, subjects, years, terms, classes, me, isManager, can, isLoading, isError, onRetry, onChanged, onMark }: {
  exams?: Exam[];
  types: ExamType[];
  subjects: SubjectOption[];
  years: AcademicYear[];
  terms: AcademicTerm[];
  classes: ClassOption[];
  me?: number;
  isManager: boolean;
  can: Abilities;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onChanged: () => void;
  onMark: (examId: number) => void;
}) {
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [editing, setEditing] = useState<Exam | "new" | null>(null);
  const done = (text: string, close = false) => {
    setNotice({ tone: "success", text });
    if (close) setViewing(null);
    onChanged();
  };
  const submit = useMutation({ mutationFn: examinationService.submitResults, onSuccess: (row) => done(`${row.name}: results submitted to the head of department.`) });
  const moderate = useMutation({ mutationFn: examinationService.moderateResults, onSuccess: (row) => done(`${row.name}: moderated and passed to the head teacher.`) });
  const approve = useMutation({ mutationFn: examinationService.approveResults, onSuccess: (row) => done(`${row.name}: approved, ready to publish.`) });
  const publish = useMutation({ mutationFn: examinationService.publishExam, onSuccess: (row) => done(`${row.name}: results published to learners and parents.`) });
  const sendBack = useMutation({ mutationFn: examinationService.returnResults, onSuccess: (row) => done(`${row.name} was sent back to the subject teacher.`) });
  const remove = useMutation({ mutationFn: examinationService.deleteExam, onSuccess: () => done("The paper was deleted.", true) });
  const steps = { submit, moderate, approve, publish };
  const actions = [submit, moderate, approve, publish, sendBack, remove];

  const columns: RegisterColumn<Exam>[] = [
    { header: "Date", cell: (row) => day(row.exam_date), exportValue: (row) => day(row.exam_date), className: "whitespace-nowrap tabular-nums text-gray-700" },
    { header: "Time", cell: (row) => timeRange(row) || "—", exportValue: (row) => timeRange(row), className: "whitespace-nowrap tabular-nums text-gray-700" },
    { header: "Paper", cell: (row) => <span className="font-medium text-gray-900">{row.name}</span>, exportValue: (row) => row.name, className: "min-w-[12rem]" },
    { header: "Subject", cell: (row) => row.subject_name, exportValue: (row) => row.subject_name, className: "text-gray-700" },
    { header: "Kind", cell: (row) => typeLabel(row), exportValue: (row) => typeLabel(row), className: "text-gray-700" },
    { header: "Classes", cell: (row) => row.class_names.join(", ") || "—", exportValue: (row) => row.class_names.join(", "), className: "max-w-[12rem] truncate text-gray-700" },
    { header: "Term", cell: (row) => row.term_name || "—", exportValue: (row) => row.term_name, exportOnly: true },
    { header: "Marked", cell: (row) => markedLabel(row), exportValue: (row) => row.marked_count, align: "right", className: "whitespace-nowrap tabular-nums text-gray-700" },
    { header: "Candidates", cell: (row) => row.candidate_count, exportValue: (row) => row.candidate_count, exportOnly: true },
    { header: "Average", cell: (row) => (row.average_percentage != null ? `${row.average_percentage.toFixed(1)}%` : "—"), exportValue: (row) => (row.average_percentage != null ? row.average_percentage.toFixed(1) : ""), align: "right", className: "tabular-nums text-gray-700" },
    { header: "Results", cell: (row) => <StatusDot {...stageOf(row)} />, exportValue: (row) => stageOf(row).label },
  ];

  const waiting = (exams ?? []).some((row) => nextAction(row, can) !== null);
  const exam = viewing !== null ? exams?.find((row) => row.id === viewing) : undefined;
  const next = exam ? nextAction(exam, can) : null;
  const canChange = exam ? exam.results_status === "draft" && (isManager || exam.created_by === me) : false;
  const canSendBack = exam ? (exam.results_status === "submitted" && can.canModerate) || (exam.results_status === "moderated" && can.canApprove) : false;
  const sat = exam ? exam.marked_count - exam.absent_count : 0;

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        // Open on what is waiting for this person, or on every paper when nothing is.
        key={exams ? (waiting ? "mine" : "all") : "loading"}
        initialState={{ view: waiting ? "mine" : "all" }}
        title="Papers"
        description="Every paper you can see and where its results stand: marked, submitted, moderated, approved, published. Open one to move it on."
        actions={
          <button type="button" onClick={() => setEditing("new")} disabled={!types.length} title={types.length ? undefined : "Add a kind of exam first."} className="btn-primary h-8 min-h-8 px-3 text-xs disabled:opacity-50">
            <FilePlus2 className="mr-1.5 h-3.5 w-3.5" /> New paper
          </button>
        }
        rows={exams}
        config={paperConfig(can)}
        columns={columns}
        rowKey={(row) => row.id}
        onOpen={(row) => { actions.forEach((mutation) => mutation.reset()); setViewing(row.id); }}
        openLabel={(row) => ACTION_LABELS[nextAction(row, can) ?? ""] ?? "Open"}
        noun={["paper", "papers"]}
        exportName={{ file: "exam-papers", title: "Examination Papers" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText="No papers yet."
      />

      {exam && (
        <DetailsModal
          size="4xl"
          title={exam.name}
          headline={{ label: `${exam.subject_name} · ${typeLabel(exam)}`, value: `${day(exam.exam_date)}${timeRange(exam) ? `, ${timeRange(exam)}` : ""}`, note: [exam.class_names.join(", "), exam.venue].filter(Boolean).join(" · ") || "No classes chosen" }}
          status={stageOf(exam)}
          notice={
            exam.results_status === "draft" && exam.returned_reason ? (
              <div className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
                <p className="font-medium">Sent back by {exam.returned_by_name || "a reviewer"}{exam.returned_at ? `, ${day(exam.returned_at)}` : ""}</p>
                <p className="mt-0.5 whitespace-pre-wrap">{exam.returned_reason}</p>
              </div>
            ) : undefined
          }
          facts={[
            ["Term", exam.term_name ? `${exam.term_name}, ${exam.academic_year_name}` : exam.academic_year_name],
            ["Marks", `Out of ${exam.total_marks}, pass at ${exam.passing_marks}`],
            ["Time allowed", exam.duration_minutes ? `${exam.duration_minutes} minutes` : undefined],
            ["Candidates", exam.candidate_count ? `${exam.candidate_count} · ${markedLabel(exam)} marked · ${exam.absent_count} absent` : "None seated yet"],
            ["Average", exam.average_percentage != null ? `${exam.average_percentage.toFixed(1)}%` : undefined],
            ["Passed", sat ? `${exam.passed_count} of ${sat} who sat` : undefined],
            ["Set by", exam.created_by_name],
            [
              "The paper",
              exam.document ? (
                <a key="file" href={exam.document} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary-700 hover:underline">
                  <Download className="h-3.5 w-3.5" /> Download{exam.document_uploaded_by_name ? ` (${exam.document_uploaded_by_name})` : ""}
                </a>
              ) : "Not uploaded",
            ],
          ]}
          sections={[{ title: "Instructions to candidates", body: exam.instructions }]}
          onClose={() => setViewing(null)}
          footer={
            <>
              {canChange && !exam.marked_count && (
                <button type="button" disabled={remove.isPending} onClick={() => remove.mutate(exam.id)} className="mr-auto inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50">
                  <Trash2 className="h-4 w-4" /> Delete
                </button>
              )}
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              {canChange && <button type="button" onClick={() => setEditing(exam)} className="btn btn-outline gap-1.5"><Pencil className="h-4 w-4" /> Change</button>}
              {exam.can_mark && exam.results_status === "draft" && (
                <button type="button" onClick={() => onMark(exam.id)} className="btn btn-outline gap-1.5"><ClipboardEdit className="h-4 w-4" /> Mark sheet</button>
              )}
              {next && next !== "mark" && (
                <button type="button" disabled={steps[next as keyof typeof steps].isPending} onClick={() => steps[next as keyof typeof steps].mutate(exam.id)} className="btn btn-primary gap-1.5">
                  {next === "publish" ? <Send className="h-4 w-4" /> : next === "submit" ? <CheckCircle2 className="h-4 w-4" /> : <ShieldCheck className="h-4 w-4" />}
                  {ACTION_LABELS[next]}
                </button>
              )}
            </>
          }
        >
          {actions.map((mutation, index) => mutation.isError && mutation !== sendBack && <div key={index}>{errorLine(mutation.error, "That could not be done.")}</div>)}
          <StageTrack exam={exam} />
          {next === "mark" && <p className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-700">Enter a mark, or tick absent, for every candidate; then submit the sheet.</p>}
          {next === "publish" && <p className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-700">Publishing shows these results to learners and parents.</p>}
          <ResultsSummary exam={exam} />
          {canSendBack && (
            <ReasonPanel
              title="Send it back to the subject teacher"
              description="The sheet reopens for correction and comes back through moderation."
              label="What needs changing"
              placeholder="e.g. Question 4 was marked out of 5, not 10: please recheck P5 Blue."
              context={{ key: "examination.note", params: { kind: "return", id: exam.id } }}
              button="Send it back"
              danger
              busy={sendBack.isPending}
              error={sendBack.error}
              errorText="It could not be sent back."
              onSubmit={(reason) => sendBack.mutate({ id: exam.id, reason })}
            />
          )}
        </DetailsModal>
      )}

      {editing && (
        <PaperForm
          exam={editing === "new" ? undefined : editing}
          types={types}
          subjects={subjects}
          years={years}
          terms={terms}
          classes={classes}
          me={me}
          isManager={isManager}
          onClose={() => setEditing(null)}
          onDone={(saved) => {
            setEditing(null);
            done(editing === "new" ? `${saved.name} saved.` : `${saved.name} updated.`);
            setViewing(saved.id);
          }}
        />
      )}
    </div>
  );
}
